/**
 * SYR-107 — the code screen.
 *
 * This is the gate on all AI spend: nothing is researched until a code that we
 * emailed comes back. It also echoes the brand we derived from the site, so a
 * typo'd domain is caught here rather than after a five-minute run.
 */

import React, { useState } from 'react';
import { Button, Input, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { THEME } from '../../components/MarketerView/config/theme';

const { Text } = Typography;

interface Props {
    email: string;
    brandName?: string | null;
    targetDomain?: string | null;
    submitting: boolean;
    invalid: boolean;
    onVerify: (pin: string) => void;
    onResend: () => void;
    resent: boolean;
}

export const PinStep: React.FC<Props> = ({
    email,
    brandName,
    targetDomain,
    submitting,
    invalid,
    onVerify,
    onResend,
    resent,
}) => {
    const intl = useIntl();
    const [pin, setPin] = useState('');

    return (
        <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: THEME.textBase, margin: '0 0 8px' }}>
                <FormattedMessage
                    id="publicHypothesis.pin.title"
                    defaultMessage="Check your email"
                />
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155', margin: '0 0 8px' }}>
                <FormattedMessage
                    id="publicHypothesis.pin.subtitle"
                    defaultMessage="We sent a six-digit code to {email}. Enter it and we'll start."
                    values={{ email: <strong>{email}</strong> }}
                />
            </p>

            {brandName ? (
                <p style={{ fontSize: 14, color: THEME.textSecondary, margin: '0 0 20px' }}>
                    <FormattedMessage
                        id="publicHypothesis.pin.brandEcho"
                        defaultMessage="We'll analyse {brand} at {domain}"
                        values={{
                            brand: <strong>{brandName}</strong>,
                            domain: targetDomain,
                        }}
                    />
                </p>
            ) : null}

            <Input
                size="large"
                value={pin}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                status={invalid ? 'error' : undefined}
                placeholder="000000"
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                onPressEnter={() => pin.length === 6 && onVerify(pin)}
                style={{
                    fontSize: 24,
                    letterSpacing: 8,
                    textAlign: 'center',
                    fontWeight: 700,
                    marginBottom: 8,
                }}
            />

            {invalid ? (
                <p style={{ fontSize: 13, color: '#dc2626', margin: '0 0 12px' }}>
                    <FormattedMessage
                        id="publicHypothesis.pin.invalid"
                        defaultMessage="That code did not work. Check the latest email, because codes expire and only the newest one is live."
                    />
                </p>
            ) : null}

            <div style={{ textAlign: 'center' }}>
            <Button
                type="primary"
                size="large"
                loading={submitting}
                disabled={pin.length !== 6}
                onClick={() => onVerify(pin)}
                style={{
                    background: THEME.primary,
                    borderColor: THEME.primary,
                    borderRadius: 8,
                    height: 'auto',
                    padding: '13px 26px',
                    fontSize: 16,
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(245, 158, 11, .35)',
                    marginTop: 8,
                }}
            >
                <FormattedMessage id="publicHypothesis.pin.submit" defaultMessage="Start the analysis" />
            </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
                {resent ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        <FormattedMessage
                            id="publicHypothesis.pin.resent"
                            defaultMessage="Sent. Give it a moment."
                        />
                    </Text>
                ) : (
                    <Button type="link" size="small" onClick={onResend}>
                        <FormattedMessage
                            id="publicHypothesis.pin.resend"
                            defaultMessage="Send it again"
                        />
                    </Button>
                )}
            </div>
        </div>
    );
};
